import { useRef, useState, type ReactNode } from 'react';
import { EmailLink } from '@/components/ui/TextLink';
import { AlertIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  isEmbeddedDelivery,
  isTerminalStatus,
  signerSummary,
  signerCount,
} from '@/lib/docusign';
import { formatDateTime, orDash } from '@/lib/format';
import type { DocuSignEnvelope } from '@/lib/types';
import { Button, ConfirmDialog, Field, Modal, Textarea } from '@/components/ui';

/*
 * Voiding a signature request.
 *
 * `POST /docusign/envelopes/{id}/void` takes `{ reason }`, 3–200 characters,
 * `.strict()`. Three things about it shape everything below.
 *
 * 1. **The reason is shown to the SIGNER.** DocuSign requires it and emails it
 *    to whoever was asked to sign, word for word. The backend states it
 *    plainly: it is not a field to fill with "cancelled". So the fact that a
 *    customer reads this sits AT THE BOX, not in a preamble somebody scrolled
 *    past, and it is never pre-filled — a default here does not save typing, it
 *    becomes the sentence a customer receives.
 * 2. **Voiding is terminal.** `voided` is one of the three terminal statuses;
 *    there is no un-void and no delete. If the document still needs signing, a
 *    NEW envelope has to be sent. The confirmation says so rather than leaving
 *    someone to discover it.
 * 3. **Status is only ever a LAST KNOWN value.** There is no webhook — an
 *    envelope's status updates when something calls `/refresh`. So a signature
 *    can complete between this page loading and this button being pressed, and
 *    the server answers `409`. Hiding the control on a terminal envelope is an
 *    optimisation; the 409 branch is the guarantee. Same reasoning this repo
 *    applies to every `ability` gate.
 *
 *    ⚠️ **The background worker makes that race MORE likely, not less** —
 *    corrected 2026-08-30. It calls the same `/refresh` on a timer, so an
 *    envelope can go terminal while this dialog is open with nobody having
 *    touched it. The 409 branch was already the guarantee and is now the one
 *    that actually fires; see `BACKGROUND_CHECK_NOTE` in `@/lib/docusign`.
 *
 * Nothing here talks to DocuSign. `api` calls OUR API, which holds the RSA key.
 */

/** The contract's own bounds, on the trimmed string — zod trims before it counts. */
const MIN_REASON = 3;
const MAX_REASON = 200;

/**
 * The keys this dialog actually sends, for `routeFieldErrors`.
 *
 * One key, because the write schema has one. Anything else the server names
 * belongs in the banner rather than on the only input on screen — see the
 * catch below.
 */
const WRITE_KEYS = ['reason'] as const;

/**
 * Whether there is anything left to void.
 *
 * ⚠️ **Deliberately NOT exported, and the control that OPENS this dialog must
 * not import it.** The rule belongs to `@/lib/docusign` and already lives
 * there: gate the trigger on the envelope's own `terminal`, with
 * `isTerminalStatus` as the same backstop used below. Re-exporting a predicate
 * from a component file would make this the second home for a rule that has
 * one, and would be the only fast-refresh violation in `src/`.
 *
 * `terminal` is the server's own computed answer and is the primary signal;
 * `isTerminalStatus` — the shared list, not a copy of it — is a backstop for a
 * response that predates the field. Either saying "closed" is enough, which
 * fails safe in the direction that matters: the cost of not offering a void
 * that would have worked is a page refresh, and the cost of offering one that
 * cannot is a confusing refusal.
 */
function canVoidEnvelope(envelope: DocuSignEnvelope): boolean {
  return !(envelope.terminal || isTerminalStatus(envelope.status));
}

/** Names the record in a sentence. Never empty — an unnamed record is unconfirmable. */
function envelopeLabel(envelope: DocuSignEnvelope): string {
  if (envelope.quote.quoteNumber) return `quote ${envelope.quote.quoteNumber}`;
  if (envelope.quote.subject) return envelope.quote.subject;
  if (envelope.emailSubject) return envelope.emailSubject;
  return `envelope #${envelope.id}`;
}

/** The signer as a person, for copy that has to address somebody. */
function signerName(envelope: DocuSignEnvelope): string {
  // Voiding cancels the request for EVERY signer, so the copy has to name them
  // all. Naming only the first understates what the button does.
  return signerSummary(envelope);
}

/** One labelled fact. Matches `DocuSignCard`'s pairs rather than inventing a second shape. */
function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-[0.08em] text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {value}
      </dd>
    </div>
  );
}

export interface VoidEnvelopeDialogProps {
  open: boolean;
  onClose: () => void;
  envelope: DocuSignEnvelope;
  /**
   * The envelope as the server sees it after the void, so the parent can
   * replace its copy rather than re-fetching. The API re-reads the status from
   * DocuSign rather than assuming `voided`, so this is the real answer.
   */
  onVoided: (envelope: DocuSignEnvelope) => void;
}

/**
 * Unmounted while closed rather than held at `open={false}`, so every piece of
 * state below is seeded on mount and there is no reset effect to forget a field
 * in. Reopening is a fresh mount — which is what someone expects after
 * cancelling out of a destructive action.
 */
export function VoidEnvelopeDialog({ open, ...rest }: VoidEnvelopeDialogProps) {
  if (!open) return null;
  return <VoidEnvelopeForm {...rest} />;
}

function VoidEnvelopeForm({
  onClose,
  envelope,
  onVoided,
}: Omit<VoidEnvelopeDialogProps, 'open'>) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /** A 422 on the one key this form renders. Bound to the textarea. */
  const [fieldError, setFieldError] = useState<string | null>(null);
  /**
   * Everything the form cannot mark: a 403 (which carries no `details` at
   * all), an unknown-key rejection, a network failure. Rendered as a banner,
   * ALONGSIDE `fieldError` rather than instead of it — both can be true at
   * once, and a `??` between them silently drops one.
   */
  const [formMessages, setFormMessages] = useState<string[]>([]);
  /**
   * The `409`: something reached a terminal state after this screen was drawn.
   * Held separately because it is not a correction to make — it withdraws the
   * action entirely, so the Void button goes rather than staying and failing.
   */
  const [refusal, setRefusal] = useState<string | null>(null);

  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const voidable = canVoidEnvelope(envelope);
  const label = envelopeLabel(envelope);
  const signer = signerName(envelope);
  /*
   * ⚠️ HOW the signer receives the cancellation depends on `delivery`, and
   * flatly promising an email would be false for half the envelopes in the
   * system. On `embedded` DocuSign emails NOBODY — we deliver the link — so a
   * confirmation saying "they will be emailed" would leave somebody waiting for
   * a message that was never going to be sent, and never chasing the customer
   * themselves. Driven by the server's own field rather than by a belief about
   * it, which is why this reads `delivery` instead of guessing from the signer.
   *
   * What is true in BOTH branches, and is the fact this dialog exists to
   * deliver: the reason is required by DocuSign and is what the signer is shown.
   */
  const emailed = !isEmbeddedDelivery(envelope);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON;
  const tooLong = trimmed.length > MAX_REASON;
  /* No `maxLength` on the box: silently truncating a sentence a customer reads
   * is worse than refusing to send it. The count is shown instead, and it turns
   * red rather than swallowing characters. */
  const canContinue = !tooShort && !tooLong && !submitting && !refusal;

  function submit() {
    setSubmitting(true);
    setFieldError(null);
    setFormMessages([]);

    api
      .voidDocusignEnvelope(envelope.id, { reason: trimmed })
      .then(({ data }) => {
        setSubmitting(false);
        setConfirming(false);
        onVoided(data);
        onClose();
      })
      .catch((caught: unknown) => {
        setSubmitting(false);
        // Back to the composing step: the confirmation has been answered, and
        // whatever comes next is read and acted on there.
        setConfirming(false);

        /*
         * 401 is left entirely to `api.ts`, which attempts one refresh and then
         * ends the session. A message here would contradict it midway through
         * signing out.
         */
        if (caught instanceof ApiError && caught.status === 401) return;

        if (caught instanceof ApiError && caught.status === 409) {
          /*
           * The status this screen was drawn from is stale — a signature
           * completed, or somebody else voided it. The server's message names
           * the state it actually found, which is the only thing that explains
           * why the button has gone, so it goes out verbatim.
           */
          setRefusal(caught.message);
          return;
        }

        if (caught instanceof ApiError) {
          const { fields, unroutable } = caught.routeFieldErrors(WRITE_KEYS);
          const banner = [...unroutable];
          /*
           * A 403 carries no `details` at all, so it produces neither a field
           * error nor an unroutable one. Falling back to the server's own
           * message is what keeps it from failing silently — and it must be a
           * banner, never a field error: the role is the problem, not the
           * sentence somebody typed.
           */
          if (!fields.reason && banner.length === 0) banner.push(caught.message);
          setFieldError(fields.reason ?? null);
          setFormMessages(banner);
          return;
        }

        setFormMessages([
          caught instanceof Error ? caught.message : 'Could not void this signature request.',
        ]);
      });
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={voidable ? 'Void this signature request' : 'This signature request is closed'}
        description={`For ${label}, sent to ${signer}.`}
        size="md"
        // There is typed text in here that a clipped backdrop must not discard.
        dismissOnBackdrop={false}
        initialFocusRef={voidable ? reasonRef : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              {voidable && !refusal ? 'Cancel' : 'Close'}
            </Button>
            {/*
             * Absent, not disabled, once there is nothing to void — on an
             * envelope that arrived terminal and on the 409 that says it became
             * terminal since. A control whose only possible outcome is an error
             * is a control nobody should be shown.
             */}
            {voidable && !refusal && (
              <Button
                variant="danger"
                onClick={() => setConfirming(true)}
                disabled={!canContinue}
                loading={submitting}
              >
                Void envelope…
              </Button>
            )}
          </>
        }
      >
        <div className="grid gap-4">
          {refusal && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-soft px-3 py-2.5 text-warning"
            >
              <AlertIcon />
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold [overflow-wrap:anywhere]">{refusal}</p>
                <p className="m-0 mt-1 text-xs [overflow-wrap:anywhere]">
                  Nothing was voided. Re-open the record to see where it stands now.
                </p>
              </div>
            </div>
          )}

          {formMessages.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger"
            >
              {formMessages.map((message) => (
                <p key={message} className="m-0 min-w-0 [overflow-wrap:anywhere]">
                  {message}
                </p>
              ))}
            </div>
          )}

          {!voidable && !refusal && (
            <p className="m-0 text-sm text-fg-secondary">
              This envelope is <span className="font-medium text-fg">{envelope.status}</span>, so
              there is nothing left to cancel. Send a new request if it still needs signing.
            </p>
          )}

          <div className="grid gap-2">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Fact
                label={signerCount(envelope) === 1 ? 'Signer' : 'Signers'}
                value={orDash(signerSummary(envelope))}
              />
              <Fact
                label={signerCount(envelope) === 1 ? 'Signer email' : 'First signer email'}
                value={
                  envelope.signer.email ? (
                    <EmailLink address={envelope.signer.email} />
                  ) : (
                    orDash(envelope.signer.email)
                  )
                }
              />
              <Fact label="Last known status" value={envelope.status} />
              <Fact
                label="Status checked"
                value={
                  envelope.statusCheckedAt ? formatDateTime(envelope.statusCheckedAt) : 'Never'
                }
              />
              <Fact label="DocuSign envelope" value={envelope.envelopeId} />
            </dl>
            {/*
             * ⛔ **`STATUS_IS_LAST_KNOWN_NOTE` was removed here — owner's
             * helper-text ruling, 2026-09-05.** It read as the counterweight to
             * the status above it: there is no webhook, so what is above is the
             * answer to a question somebody asked at some point, and **a status
             * with no check time beside it reads as current.**
             *
             * ⚠️ **That belief is exactly the one this dialog can act on
             * wrongly, and it is now unguarded on screen.** The 409 branch above
             * is what catches it when it happens — a refusal, so it stays — but
             * it fires AFTER somebody has pressed Void rather than before.
             */}
          </div>

          {voidable && !refusal && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                /*
                 * This dialog can be opened from inside another form. It is
                 * portalled to the body, but React propagates by the REACT
                 * tree, so an un-stopped submit reaches the form behind it and
                 * saves a half-filled record. That bug has shipped from this
                 * repo before.
                 */
                event.stopPropagation();
                if (canContinue) setConfirming(true);
              }}
            >
              <Field label="Reason for cancelling" required error={fieldError}>
                {(fieldProps) => (
                  <div className="grid gap-1.5">
                    {/*
                     * At the box, not in a preamble. DocuSign requires this
                     * text and sends it to the signer verbatim, so the person
                     * typing has to know they are writing to a customer BEFORE
                     * they write "cancelled".
                     *
                     * Deliberately NOT the Field's `hint`: `Field` renders a
                     * hint only while there is no error, so a validation
                     * failure would take this warning off the screen at the
                     * exact moment somebody is rewriting the sentence.
                     */}
                    <div
                      id={`${fieldProps.id}-notice`}
                      className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-warning"
                    >
                      <AlertIcon />
                      <p className="m-0 min-w-0 text-xs [overflow-wrap:anywhere]">
                        <span className="font-semibold">{signer} will read this</span>
                        {emailed ? ', in the cancellation email' : ''} — exactly as typed.
                      </p>
                    </div>

                    <Textarea
                      {...fieldProps}
                      aria-describedby={
                        cx(fieldProps['aria-describedby'], `${fieldProps.id}-notice`) || undefined
                      }
                      ref={reasonRef}
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value);
                        // The server judged a different string; its verdict no
                        // longer describes what is in the box.
                        setFieldError(null);
                      }}
                      invalid={Boolean(fieldError) || tooLong}
                      autoGrow={{ minRows: 3, maxRows: 6 }}
                    />

                    <p
                      className={cx(
                        'm-0 text-xs',
                        tooLong ? 'font-medium text-danger' : 'text-fg-subtle',
                      )}
                    >
                      {tooLong
                        ? `${trimmed.length} characters — ${trimmed.length - MAX_REASON} too many. DocuSign accepts ${MAX_REASON}.`
                        : `${trimmed.length}/${MAX_REASON} characters. At least ${MIN_REASON}.`}
                    </p>
                  </div>
                )}
              </Field>
            </form>
          )}
        </div>
      </Modal>

      {/*
       * The repo's confirmation convention, and it earns the extra step here:
       * it is the only place the person is shown the exact sentence their
       * customer will receive, quoted back at them, beside what voiding costs.
       */}
      <ConfirmDialog
        open={confirming}
        // Back rather than Cancel — the typed reason is still there, and
        // discarding it on a second thought would be its own small failure.
        onClose={() => setConfirming(false)}
        cancelLabel="Back"
        onConfirm={submit}
        loading={submitting}
        tone="danger"
        title="Void this signature request?"
        confirmLabel="Void envelope"
        description={
          <div className="grid gap-2">
            <p className="m-0">
              <span className="font-medium text-fg">{signer}</span> can no longer sign{' '}
              <span className="font-medium text-fg">{label}</span>. DocuSign withdraws the
              request
              {emailed
                ? ' and emails them to say it was cancelled, giving this as the reason:'
                : '. Their signing link stops working, and this is the reason they are shown:'}
            </p>
            <p className="m-0 rounded-md border border-border bg-surface-sunken px-3 py-2 text-fg [overflow-wrap:anywhere]">
              “{trimmed}”
            </p>
            <p className="m-0">
              <span className="font-medium text-fg">This is final.</span> Send a new request if it
              still needs signing. The record stays on {label}.
            </p>
          </div>
        }
      />
    </>
  );
}
